import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { REMOTE_API } from '../../config/request.config';

interface Registration {
  id: number;
  code: string;
  user_totem: string;
  lead_nome: string;
  lead_telefone: string;
  prize: string;
  prize_number: number;
  created_at: string;
}

interface ApiResponse {
  total: number;
  syncedAt: string;
  records: Registration[];
}

const formatDateTime = (raw: string): string => {
  if (!raw) return '—';
  try {
    // SQLite stores as "YYYY-MM-DD HH:MM:SS" (UTC)
    const dt = new Date(raw.replace(' ', 'T') + 'Z');
    return dt.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'America/Sao_Paulo',
    });
  } catch {
    return raw;
  }
};

export default function SyncedRegistrations() {
  const navigate = useNavigate();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [lastFetch, setLastFetch] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<ApiResponse>(`${REMOTE_API}/v1/subscription/synced`, { timeout: 10000 });
      setData(res.data);
      setLastFetch(new Date().toLocaleTimeString('pt-BR'));
    } catch (err: any) {
      setError('Não foi possível conectar ao servidor. Verifique a conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = (data?.records ?? []).filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.lead_nome?.toLowerCase().includes(q) ||
      r.lead_telefone?.toLowerCase().includes(q) ||
      r.prize?.toLowerCase().includes(q) ||
      r.code?.toLowerCase().includes(q) ||
      r.user_totem?.toLowerCase().includes(q)
    );
  });

  const csvUrl = `${REMOTE_API}/v1/subscription/export/csv`;

  return (
    <div style={{
      width: '100%', height: '100vh',
      backgroundColor: '#0f1923', color: '#FFFFFF',
      fontFamily: 'Arial, sans-serif',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a2740', padding: '24px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '3px solid #5b9bd5', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#5b9bd5' }}>
            📋 Cadastros Sincronizados
          </div>
          <div style={{ fontSize: '20px', color: '#7799bb', marginTop: '4px' }}>
            Dados recebidos pelo servidor remoto — EXPODIRETO Softys 2026
          </div>
        </div>
        <button
          onClick={() => navigate('/admin')}
          style={{
            padding: '14px 28px', fontSize: '22px', fontWeight: 'bold',
            backgroundColor: '#2a3a50', color: '#fff', border: '2px solid #5b9bd5',
            borderRadius: '10px', cursor: 'pointer',
          }}
        >
          ← Voltar
        </button>
      </div>

      {/* Controls */}
      <div style={{
        padding: '24px 40px', backgroundColor: '#101e2c',
        display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
        borderBottom: '1px solid #1a2740',
      }}>
        {/* Total badge */}
        <div style={{
          backgroundColor: '#5b9bd5', borderRadius: '10px', padding: '12px 24px',
          fontSize: '24px', fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap',
        }}>
          Total: {data?.total ?? '—'} cadastros
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Buscar por nome, telefone, prêmio, totem..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: '280px', padding: '12px 20px', fontSize: '22px',
            backgroundColor: '#1a2740', color: '#fff', border: '2px solid #2a3a50',
            borderRadius: '10px', outline: 'none',
          }}
        />

        {/* Refresh */}
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: '12px 28px', fontSize: '22px', fontWeight: 'bold',
            backgroundColor: loading ? '#2a3a50' : '#1a7a4a', color: '#fff',
            border: '2px solid #27ae60', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? '⏳ Carregando...' : '🔄 Atualizar'}
        </button>

        {/* Export CSV */}
        <a
          href={csvUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            padding: '12px 28px', fontSize: '22px', fontWeight: 'bold',
            backgroundColor: '#1a4a7a', color: '#fff',
            border: '2px solid #2980b9', borderRadius: '10px',
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          ⬇ Exportar CSV
        </a>
      </div>

      {lastFetch && (
        <div style={{ padding: '8px 40px', fontSize: '18px', color: '#7799bb', backgroundColor: '#0f1923' }}>
          Última atualização: {lastFetch} · {filtered.length} registro(s) exibidos
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          margin: '24px 40px', padding: '20px', backgroundColor: '#3a1a1a',
          border: '2px solid #e74c3c', borderRadius: '10px', fontSize: '22px', color: '#e74c3c',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 40px 40px' }}>
        {!loading && !error && data && (
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '20px',
            marginTop: '20px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#1a2740', position: 'sticky', top: 0, zIndex: 1 }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Nome</th>
                <th style={thStyle}>Telefone</th>
                <th style={thStyle}>Prêmio</th>
                <th style={thStyle}>Totem</th>
                <th style={thStyle}>Data/Hora Cadastro</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#7799bb', fontSize: '24px' }}>
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr
                    key={r.id}
                    style={{ backgroundColor: i % 2 === 0 ? '#101e2c' : 'transparent' }}
                  >
                    <td style={tdStyle}>{r.id}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{r.lead_nome || '—'}</td>
                    <td style={tdStyle}>{r.lead_telefone || '—'}</td>
                    <td style={{ ...tdStyle, color: '#5b9bd5', fontWeight: 'bold' }}>{r.prize || '—'}</td>
                    <td style={{ ...tdStyle, color: '#aab' }}>{r.user_totem || '—'}</td>
                    <td style={{ ...tdStyle, color: '#7dc18a', whiteSpace: 'nowrap' }}>
                      {formatDateTime(r.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '16px 20px', textAlign: 'left', fontSize: '22px', fontWeight: 'bold',
  color: '#5b9bd5', borderBottom: '2px solid #2a3a50',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid #1a2740', fontSize: '20px',
};
